import { useState, useCallback } from 'react';
import {
  BarChart3,
  Send,
  Loader2,
  Sparkles,
  AlertCircle,
  Table,
} from 'lucide-react';
import { insightsApi, type InsightsResponse, type Metric } from '@/services/api';
import { useEffect } from 'react';

export function InsightsPage() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InsightsResponse | null>(null);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Fetch available metrics on mount
  useEffect(() => {
    setMetricsLoading(true);
    insightsApi
      .listMetrics()
      .then(({ metrics }) => setMetrics(metrics))
      .catch(() => {})
      .finally(() => setMetricsLoading(false));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!query.trim()) return;
      setLoading(true);
      setError(null);
      setResult(null);
      try {
        const res = await insightsApi.query({ query: query.trim() });
        setResult(res);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Query failed');
      } finally {
        setLoading(false);
      }
    },
    [query],
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-navy-900">Insights Engine</h1>
        <p className="text-sm text-slate-500 mt-1">
          Query the Cube.dev semantic layer with natural language. The LangGraph
          insights agent translates your questions into analytical queries with
          cognitive augmentation.
        </p>
      </div>

      {/* Query form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-navy-900 text-sm mb-4 flex items-center gap-2">
          <Sparkles size={16} className="text-brand-teal" />
          Natural Language Query
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g. "What are the top engagement trends for health-conscious parents in Q1?"'
            rows={3}
            className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal"
          />
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <AlertCircle size={14} />
              {error}
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="inline-flex items-center gap-2 px-5 py-2 bg-brand-teal text-navy-900 font-semibold text-sm rounded-lg hover:bg-brand-teal/90 disabled:opacity-40 transition-colors shadow-sm"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
              Query
            </button>
          </div>
        </form>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm animate-slide-up">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-navy-900 text-sm">Result</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
              {result.answer}
            </div>

            {result.data && result.data.length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Table size={12} />
                  Data
                </h3>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50">
                        {Object.keys(result.data[0]).map((key) => (
                          <th
                            key={key}
                            className="px-3 py-2 text-left font-semibold text-slate-600 border-b border-slate-200"
                          >
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.data.map((row, i) => (
                        <tr
                          key={i}
                          className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                        >
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="px-3 py-2 text-slate-700">
                              {String(val ?? '—')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Available metrics */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-brand-teal/10 flex items-center justify-center text-brand-teal border border-brand-teal/20">
            <BarChart3 size={16} />
          </div>
          <h2 className="font-semibold text-navy-900 text-sm">
            Available Metrics
            {metrics.length > 0 && (
              <span className="ml-2 text-xs text-slate-400 font-normal">
                ({metrics.length})
              </span>
            )}
          </h2>
        </div>

        {metricsLoading ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            Loading metrics...
          </div>
        ) : metrics.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <p className="text-sm text-slate-500">
              No Cube.dev metrics available. Ensure Cube.dev is configured.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {metrics.map((m) => (
              <div
                key={m.name}
                className="bg-white rounded-lg border border-slate-200 px-4 py-3 hover:border-brand-teal/30 transition-colors"
              >
                <p className="text-sm font-semibold text-navy-900">
                  {m.title}
                </p>
                <p className="text-[11px] font-mono text-slate-400">
                  {m.name}
                </p>
                {m.description && (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {m.description}
                  </p>
                )}
                <span className="inline-block mt-2 text-[9px] uppercase font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                  {m.type}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
